import { useEffect, useState, useCallback } from "react";
import { History, CalendarClock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

interface ExtensionRow {
  id: string;
  days_extended: number;
  previous_trial_end: string | null;
  new_trial_end: string;
  created_at: string;
}

/**
 * Read-only history of self-serve trial extensions on the current
 * subscription's environment. Renders nothing until at least one
 * extension exists. Exposes a `refreshKey` prop so parents can force
 * a reload after a successful extension.
 */
export function TrialExtensionHistory({ refreshKey = 0 }: { refreshKey?: number }) {
  const { userId, mode, isAuthenticated } = useAuth();
  const [rows, setRows] = useState<ExtensionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAuthenticated || mode !== "real" || !userId) {
      setRows([]); setLoading(false); return;
    }
    const { data } = await supabase
      .from("trial_extensions" as any)
      .select("id, days_extended, previous_trial_end, new_trial_end, created_at")
      .eq("user_id", userId)
      .eq("environment", getPaddleEnvironment())
      .order("created_at", { ascending: false });
    setRows((data as unknown as ExtensionRow[]) ?? []);
    setLoading(false);
  }, [userId, mode, isAuthenticated]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading || rows.length === 0) return null;

  const latest = rows[0];
  const totalDays = rows.reduce((s, r) => s + r.days_extended, 0);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
          <History className="h-3.5 w-3.5" /> Trial extension history
        </div>
        {rows.length > 1 && (
          <span className="text-[11px] text-muted-foreground">
            {rows.length} extensions · +{totalDays} days total
          </span>
        )}
      </div>

      <div className="flex items-start gap-2 text-sm">
        <CalendarClock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            +{latest.days_extended} day{latest.days_extended === 1 ? "" : "s"} added
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              on {new Date(latest.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            New trial end:{" "}
            <span className="text-foreground">
              {new Date(latest.new_trial_end).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </span>
          </p>
        </div>
      </div>

      {rows.length > 1 && (
        <ul className="pt-1 border-t border-border/50 space-y-1">
          {rows.slice(1).map((r) => (
            <li key={r.id} className="text-xs text-muted-foreground flex items-center justify-between gap-2">
              <span>+{r.days_extended} days</span>
              <span>{new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}